import { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import type { StagingItem, ValidationStatus } from './stagingApi';

interface StagingReviewTableProps {
  items: StagingItem[];
}

const STATUS_COLORS: Record<ValidationStatus, 'default' | 'success' | 'warning' | 'error'> = {
  valid: 'success',
  warning: 'warning',
  error: 'error',
};

const formatPrice = (cents: number): string => {
  if (cents === 0) return '$0.00';
  return `$${(cents / 100).toFixed(2)}`;
};

const ExpandableRow = ({ item }: { item: StagingItem }) => {
  const [open, setOpen] = useState(false);
  const hasDetails = item.validationErrors.length > 0;

  return (
    <>
      <TableRow
        sx={{
          '&:last-child td': { borderBottom: 0 },
          backgroundColor: item.validationStatus === 'error' ?
            'error.50' :
            item.validationStatus === 'warning' ?
              'warning.50' :
              undefined,
        }}
      >
        <TableCell sx={{ width: 40, p: 0.5 }}>
          {hasDetails && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{item.rowNumber}</TableCell>
        <TableCell>
          <Chip
            label={item.validationStatus}
            size="small"
            color={STATUS_COLORS[item.validationStatus]}
            variant="filled"
          />
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{item.parentSku}</TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{item.skuSubcode ?? ''}</TableCell>
        <TableCell>{item.color ?? ''}</TableCell>
        <TableCell>{item.sizeCode ?? ''}</TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{item.yadjustedCode ?? ''}</TableCell>
        <TableCell align="right">{formatPrice(item.price)}</TableCell>
        <TableCell align="right">{item.affectedCatalogItemCount}</TableCell>
        <TableCell align="right">{item.affectedInventoryCount}</TableCell>
      </TableRow>
      {hasDetails && (
        <TableRow>
          <TableCell sx={{ py: 0, borderBottom: open ? undefined : 'none' }} colSpan={11}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ py: 1, pl: 5 }}>
                {item.validationErrors.map((err, i) => (
                  <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                    <Chip
                      label={err.code}
                      size="small"
                      variant="outlined"
                      color={item.validationStatus === 'error' ? 'error' : 'warning'}
                      sx={{ mr: 1, fontFamily: 'monospace', fontSize: 11 }}
                    />
                    {err.message}
                  </Typography>
                ))}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

type StatusFilter = 'all' | ValidationStatus;

const StagingReviewTable = ({ items }: StagingReviewTableProps) => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const sortedItems = useMemo(() => {
    const order: Record<ValidationStatus, number> = { error: 0, warning: 1, valid: 2 };
    return [...items].sort((a, b) => order[a.validationStatus] - order[b.validationStatus]);
  }, [items]);

  const filteredItems = useMemo(
    () => (statusFilter === 'all' ? sortedItems : sortedItems.filter(i => i.validationStatus === statusFilter)),
    [sortedItems, statusFilter],
  );

  const paginatedItems = useMemo(
    () => filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredItems, page, rowsPerPage],
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status filter</InputLabel>
          <Select
            value={statusFilter}
            label="Status filter"
            onChange={(e) => {
              setStatusFilter(e.target.value as StatusFilter);
              setPage(0);
            }}
          >
            <MenuItem value="all">
              All (
              {items.length}
              )
            </MenuItem>
            <MenuItem value="error">
              Errors (
              {items.filter(i => i.validationStatus === 'error').length}
              )
            </MenuItem>
            <MenuItem value="warning">
              Warnings (
              {items.filter(i => i.validationStatus === 'warning').length}
              )
            </MenuItem>
            <MenuItem value="valid">
              Valid (
              {items.filter(i => i.validationStatus === 'valid').length}
              )
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell sx={{ fontWeight: 600 }}>Row</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Parent SKU</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>SKU Subcode</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Color</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Override Code</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Price</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Catalog Items</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Inventory</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedItems.map(item => (
              <ExpandableRow key={item.id} item={item} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredItems.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Box>
  );
};

export default StagingReviewTable;
